import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { IconButton } from "@/components/ui/Button";

const STATUS_QUERY_ALIASES: Record<string, string> = {
  bidding: "invitation-issued",
  invitation: "invitation-issued",
  award: "award-approved",
  contract: "contract-management",
  cancelled: "project-cancelled",
  canceled: "project-cancelled",
  tor: "tor-prep",
  purchase: "purchase-request",
};

const METHOD_QUERY_ALIASES: Record<string, string> = {
  "specific-method": "specific",
  "price-inquiry": "price-survey",
};

function parseSearchQuery(rawQuery: string) {
  const params = new URLSearchParams();
  const freeTextTerms: string[] = [];

  for (const token of rawQuery.split(/\s+/).filter(Boolean)) {
    const separatorIndex = token.indexOf(":");
    if (separatorIndex <= 0) {
      freeTextTerms.push(token);
      continue;
    }

    const key = token.slice(0, separatorIndex).toLowerCase();
    const rawValue = token.slice(separatorIndex + 1).trim();
    if (!rawValue) {
      continue;
    }

    const normalizedValue = rawValue.toLowerCase();

    if (key === "status") {
      params.append(
        "status",
        STATUS_QUERY_ALIASES[normalizedValue] || normalizedValue,
      );
      continue;
    }

    if (key === "province") {
      params.set("province", normalizedValue);
      continue;
    }

    if (key === "method") {
      params.append(
        "method",
        METHOD_QUERY_ALIASES[normalizedValue] || normalizedValue,
      );
      continue;
    }

    freeTextTerms.push(token);
  }

  if (freeTextTerms.length > 0) {
    params.set("q", freeTextTerms.join(" "));
  }

  return params;
}

export function TopBar() {
  const { toggleSidebar, setSearchOpen } = useUIStore();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  return (
    <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="h-full px-4 flex items-center justify-between gap-4">
        {/* Left section */}
        <div className="flex items-center gap-3">
          <IconButton
            icon="solar:hamburger-menu-line-duotone"
            label="เปิด/ปิดแถบด้านข้าง"
            variant="ghost"
            onClick={toggleSidebar}
            className="lg:hidden"
          />

          {/* Logo */}
          <div className="flex items-center gap-2">
            <img
              src="/logo.jpg"
              alt="G-Wiz"
              className="w-8 h-8 rounded-lg object-cover"
            />
            <span className="font-display font-bold text-xl text-slate-900 hidden sm:block">
              G-Wiz | PQ
            </span>
          </div>
        </div>

        {/* Search - desktop */}
        <button
          onClick={() => setSearchOpen(true)}
          className="hidden md:flex items-center gap-3 px-4 py-2 w-80 text-left text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        >
          <Icon icon="solar:magnifer-line-duotone" className="w-5 h-5" />
          <span className="flex-1 text-sm">ค้นหาประกาศ...</span>
          <kbd className="px-1.5 py-0.5 text-xs font-medium bg-white rounded border border-slate-200">
            ⌘K
          </kbd>
        </button>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Search - mobile */}
          <IconButton
            icon="solar:magnifer-line-duotone"
            label="ค้นหา"
            variant="ghost"
            onClick={() => setSearchOpen(true)}
            className="md:hidden"
          />

          {/* User menu */}
          <details className="relative ml-2 group">
            <summary className="list-none flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 [&::-webkit-details-marker]:hidden">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.email?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <Icon
                icon="solar:alt-arrow-down-line-duotone"
                className="w-4 h-4 text-slate-400 hidden sm:block transition-transform group-open:rotate-180"
              />
            </summary>

            <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate("/login", { replace: true });
                }}
                className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              >
                <Icon
                  icon="solar:logout-3-line-duotone"
                  className="h-4 w-4 text-slate-500"
                />
                <span>ออกจากระบบ</span>
              </button>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}

// Global search modal
export function SearchModal() {
  const navigate = useNavigate();
  const { searchOpen, setSearchOpen, searchQuery, setSearchQuery } =
    useUIStore();

  if (!searchOpen) return null;

  const handleSearch = () => {
    const query = searchQuery.trim();
    const params = parseSearchQuery(query);

    navigate({
      pathname: "/tenders",
      search: params.toString() ? `?${params.toString()}` : "",
    });
    setSearchOpen(false);
  };

  const handleQuickNavigate = (path: string) => {
    navigate(path);
    setSearchOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
        onClick={() => setSearchOpen(false)}
      />

      {/* Modal */}
      <div className="relative max-w-2xl mx-auto mt-20 bg-white rounded-2xl shadow-2xl animate-slide-up">
        {/* Search input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          className="flex items-center gap-3 px-4 border-b border-slate-200"
        >
          <Icon
            icon="solar:magnifer-line-duotone"
            className="w-5 h-5 text-slate-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="ค้นหาประกาศ, บริษัท, หน่วยงาน..."
            className="no-focus-ring flex-1 py-4 text-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:shadow-none focus-visible:shadow-none"
            autoFocus
          />
          <button
            type="submit"
            className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
          >
            ค้นหา
          </button>
          <kbd
            onClick={() => setSearchOpen(false)}
            className="px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded cursor-pointer hover:bg-slate-200"
          >
            ESC
          </kbd>
        </form>

        {/* Quick links */}
        <div className="p-4">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">
            ลิงก์ด่วน
          </p>
          <div className="space-y-1">
            <SearchQuickLink
              icon="solar:document-text-line-duotone"
              label="ดูประกาศทั้งหมด"
              shortcut="⌘T"
              onClick={() => handleQuickNavigate("/tenders")}
            />
            <SearchQuickLink
              icon="solar:buildings-2-line-duotone"
              label="ดูบริษัท"
              shortcut="⌘C"
              onClick={() => handleQuickNavigate("/companies")}
            />
            <SearchQuickLink
              icon="solar:buildings-line-duotone"
              label="ดูหน่วยงาน"
              shortcut="⌘A"
              onClick={() => handleQuickNavigate("/agencies")}
            />
            <SearchQuickLink
              icon="solar:bookmark-line-duotone"
              label="รายการที่บันทึก"
              shortcut="⌘W"
              onClick={() => handleQuickNavigate("/watchlist")}
            />
          </div>
        </div>

      </div>
    </div>
  );
}

function SearchQuickLink({
  icon,
  label,
  shortcut,
  onClick,
}: {
  icon: string;
  label: string;
  shortcut: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors group"
    >
      <Icon
        icon={icon}
        className="w-5 h-5 text-slate-400 group-hover:text-blue-600"
      />
      <span className="flex-1 text-left text-slate-700">{label}</span>
      <kbd className="px-1.5 py-0.5 text-xs font-medium text-slate-400 bg-slate-100 rounded">
        {shortcut}
      </kbd>
    </button>
  );
}
